/**
 * PersonPhoto — the optional photo of a natural person, in the Person page header
 * (P24, Pedro 2026-10-10).
 *
 * The photo is a document of type PHOTO (one foundation with P23). The current photo
 * is the most recent one on file. Pedro's decisions:
 *   - changed inside Edit mode (§3g Pattern A, "for consistency's sake"): Change photo
 *     stages a preview with an orange ring; Save uploads it; Discard drops it;
 *   - maker-checker: an RM's change is filed for approval (blue) and the approver sees
 *     the picture before deciding; Admin / Compliance apply at once.
 * The picture is shrunk in the browser to ~512 px JPEG before it is staged. Earlier
 * photos stay listed on the Documents tab (nothing is overwritten).
 */
import { ActionIcon, Avatar, Badge, Button, FileButton, Group, Stack, Tooltip } from '@mantine/core';
import { IconArrowBackUp, IconCamera, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';

import type { UUID } from '../api/types';
import { useRecordDocuments, type DocumentStaging } from '../hooks/useDocuments';
import { usePermissions } from '../hooks/usePermissions';
import { shrinkImage } from '../utils/imageShrink';
import { useAlertModal } from './AlertModalProvider';
import { PendingUploadBadge } from './DocumentsPanel';
import { PendingCRBadge } from './PendingCRBadge';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

export function PersonPhoto({ personId, name, editMode, staging }: {
  personId: UUID;
  name: string;
  editMode: boolean;
  staging: DocumentStaging;
}) {
  const { can } = usePermissions();
  const canWrite = can('record.write');
  const { showAlert } = useAlertModal();
  const filesQ = useRecordDocuments('persons', personId);
  const [busy, setBusy] = useState(false);
  const { queue } = staging;

  const current = (filesQ.data?.documents ?? []).find((d) => d.kind === 'PHOTO' && !d.deleted_at);
  const filed = (filesQ.data?.pending_uploads ?? []).find((p) => p.kind === 'PHOTO');
  const stagedOp = queue.ops.find((op) => op.kind === 'upload' && op.docKind === 'PHOTO');
  const stagedFile = stagedOp?.kind === 'upload' ? stagedOp.file : null;
  const removing = !!current && queue.ops.some((op) => op.kind === 'remove' && op.document_id === current.id);

  // The staged preview is a data: URL — the app's CSP allows `img-src 'self' data:`, not
  // blob: (never weakened for a preview, C-260). The shrunk photo is small, so this is cheap.
  const [preview, setPreview] = useState<string | null>(null);
  useEffect(() => {
    if (!stagedFile) { setPreview(null); return undefined; }
    let live = true;
    const reader = new FileReader();
    reader.onload = () => { if (live) setPreview(typeof reader.result === 'string' ? reader.result : null); };
    reader.readAsDataURL(stagedFile);
    return () => { live = false; };
  }, [stagedFile]);

  const src = preview ?? (current && !removing ? `/documents/${current.id}/file?inline=true` : null);
  const active = canWrite && editMode;

  async function pick(file: File | null) {
    if (!file) return;
    if (!/^image\/(jpeg|png)$/.test(file.type) && !/\.(jpe?g|png)$/i.test(file.name)) {
      showAlert({ color: 'red', title: 'Photo not added', message: 'The photo must be a JPG or PNG image.' });
      return;
    }
    setBusy(true);
    try {
      const small = await shrinkImage(file);
      // One staged photo at a time: a new pick replaces the previous one.
      queue.transform((prev) => prev.filter((op) => !(op.kind === 'upload' && op.docKind === 'PHOTO')));
      staging.stageUpload('PHOTO', null, small);
    } catch (e) {
      showAlert({
        color: 'red', title: 'Photo not added',
        message: e instanceof Error ? e.message : 'The image could not be read.',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack gap={4} align="center">
      <Avatar
        src={src}
        alt={`Photo of ${name}`}
        size={72}
        radius="md"
        style={stagedFile || removing ? { outline: '2px solid var(--mantine-color-orange-5)' } : undefined}
      >
        {initials(name)}
      </Avatar>
      {stagedFile && <Badge color="orange" variant="light" size="xs">Pending Save</Badge>}
      {removing && <Badge color="orange" variant="light" size="xs">Pending remove</Badge>}
      {filed && <PendingUploadBadge p={filed} />}
      {current && <PendingCRBadge crs={current.pending_change_requests} />}
      {active ? (
        <Group gap={4} wrap="nowrap">
          <FileButton onChange={pick} accept="image/jpeg,image/png">
            {(props) => (
              <Button {...props} size="compact-xs" variant="light" loading={busy}
                leftSection={<IconCamera size={12} />}>
                {current || stagedFile ? 'Change photo' : 'Add photo'}
              </Button>
            )}
          </FileButton>
          {stagedFile || removing ? (
            <Tooltip label="Undo the staged photo change" withArrow>
              <ActionIcon size="sm" variant="light" color="gray" onClick={() => queue.transform(
                (prev) => prev.filter((op) => !(op.kind === 'upload' && op.docKind === 'PHOTO')
                  && !(op.kind === 'remove' && op.document_id === current?.id)),
              )}>
                <IconArrowBackUp size={12} />
              </ActionIcon>
            </Tooltip>
          ) : current && (
            <Tooltip label="Remove the photo (on Save)" withArrow>
              <ActionIcon size="sm" variant="light" color="red" aria-label="Remove the photo"
                onClick={() => queue.stage({ kind: 'remove', document_id: current.id })}>
                <IconTrash size={12} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      ) : canWrite && (
        <Tooltip label="Enter Edit mode to change the photo" withArrow>
          <span style={{ display: 'inline-flex' }}>
            <Button size="compact-xs" variant="light" disabled leftSection={<IconCamera size={12} />}>
              {current ? 'Change photo' : 'Add photo'}
            </Button>
          </span>
        </Tooltip>
      )}
    </Stack>
  );
}
